import { useState, useEffect, useCallback } from "react";
import { Button, Space, Modal, Form, Select, DatePicker, Input, message } from "antd";
import { PlusOutlined, ReloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import ExportButton, { downloadBlob } from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import useRiderOptions from "../../hooks/useRiderOptions";
import {
  listNoc, createNoc, updateNoc, deleteNoc, downloadNocPdf, exportNoc,
} from "../../services/payroll.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

export default function NOCTab() {
  const { canEdit, canDelete } = usePermission("payrollExpenses");
  const { options: nocTypeOptions, data: nocTypes } = useSettingsDropdown("noc-types");
  const { options: nocCategoryOptions } = useSettingsDropdown("noc-categories");
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: fleetOptions } = useSettingsDropdown("fleet-vehicle-types", {
    filter: (item) => item.category === "fleet",
  });
  const { options: riderOptions, loading: ridersLoading, onSearch: onRiderSearch } =
    useRiderOptions();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [nocType, setNocType] = useState();
  const [dateRange, setDateRange] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      nocType: nocType || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, nocType, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listNoc(params({ page, per_page: pageSize }));
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load NOC certificates");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, nocType, dateRange]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    if (record) {
      form.setFieldsValue({
        rider: record.rider?._id,
        nocDate: record.nocDate ? dayjs(record.nocDate) : null,
        nocType: record.nocType?._id,
        nocCategory: record.nocCategory?._id,
        project: record.project?._id,
        fleetType: record.fleetType?._id,
        uuidNo: record.uuidNo,
        civilId: record.civilId,
        title: record.title,
        addressedTo: record.addressedTo,
        authorisedSignatory: record.authorisedSignatory,
        bodyTemplate: record.bodyTemplate,
        notes: record.notes,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ nocDate: dayjs() });
    }
  };

  /**
   * Picking a NOC type pre-fills its Settings body template, which the admin can
   * then edit. What is saved on the record is the snapshot, so a later Settings
   * edit cannot change an already-issued certificate.
   */
  const handleTypeChange = (value) => {
    const type = (nocTypes || []).find((t) => t._id === value);
    if (type?.bodyTemplate && !form.getFieldValue("bodyTemplate")) {
      form.setFieldsValue({ bodyTemplate: type.bodyTemplate });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = { ...values, nocDate: values.nocDate.format("YYYY-MM-DD") };
      if (modal.record) {
        await updateNoc(modal.record._id, payload);
        message.success("NOC updated and PDF regenerated");
      } else {
        await createNoc(payload);
        message.success("NOC issued");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save NOC");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (record) => {
    try {
      const blob = await downloadNocPdf(record._id);
      downloadBlob(blob, `${record.nocTrackId}.pdf`);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not download the certificate");
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete NOC",
      content: `Delete ${record.nocTrackId}?`,
      onConfirm: async () => {
        try {
          await deleteNoc(record._id);
          message.success("NOC deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete NOC");
        }
      },
    });
  };

  const columns = [
    { title: "NOC Track ID", dataIndex: "nocTrackId" },
    { title: "NOC Date", dataIndex: "nocDateLabel" },
    {
      title: "Partner",
      dataIndex: "partnerName",
      render: (_, r) => (
        <PartnerLabel name={`${r.partnerId} — ${r.partnerName}`} deleted={r.riderDeleted} />
      ),
    },
    { title: "Civil ID", dataIndex: "civilId" },
    { title: "UUID No", dataIndex: "uuidNo" },
    { title: "NOC Type", dataIndex: "nocTypeName" },
    { title: "Category", dataIndex: "nocCategoryName" },
    { title: "Title", dataIndex: "title", ellipsis: true },
    {
      title: "PDF",
      key: "pdf",
      width: 110,
      render: (_, record) => (
        <Button
          size="small"
          icon={<FilePdfOutlined />}
          onClick={() => handleDownload(record)}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search track ID, partner, civil ID"
          allowClear
          style={{ width: 280 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="NOC Type"
          allowClear
          style={{ width: 200 }}
          options={nocTypeOptions}
          value={nocType}
          onChange={setNocType}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton moduleName="NOC_Certificates" onExport={() => exportNoc(params())} />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Issue NOC
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit NOC" : "Issue NOC"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={680}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="rider" label="Partner" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={false}
              onSearch={onRiderSearch}
              loading={ridersLoading}
              options={riderOptions}
              placeholder="Search by partner ID or name"
            />
          </Form.Item>
          <Form.Item name="nocDate" label="NOC Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="nocType" label="NOC Type" rules={[{ required: true }]}>
            <Select options={nocTypeOptions} onChange={handleTypeChange} />
          </Form.Item>
          <Form.Item name="nocCategory" label="NOC Category">
            <Select options={nocCategoryOptions} allowClear />
          </Form.Item>
          <Form.Item name="project" label="Project">
            <Select options={projectOptions} allowClear />
          </Form.Item>
          <Form.Item name="fleetType" label="Fleet Type">
            <Select options={fleetOptions} allowClear />
          </Form.Item>
          <Form.Item name="civilId" label="Civil ID">
            <Input placeholder="Defaults to the partner's stored civil ID" />
          </Form.Item>
          <Form.Item name="uuidNo" label="UUID No">
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title">
            <Input placeholder="e.g. TO WHOMSOEVER IT MAY CONCERN" />
          </Form.Item>
          <Form.Item name="addressedTo" label="Addressed To">
            <Input placeholder="e.g. The Manager, Bank Muscat" />
          </Form.Item>
          <Form.Item name="authorisedSignatory" label="Authorised Signatory">
            <Input placeholder="Name and title of the signing officer" />
          </Form.Item>
          <Form.Item
            name="bodyTemplate"
            label="Body"
            extra="Supports {{rider}}, {{civilId}}, {{uuidNo}}, {{partnerId}}, {{project}}, {{date}}."
          >
            <TextArea rows={5} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
